import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./register.css";
import { RegistrationAPI } from "./api/RegistrationAPI";

const Register = () => {
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState("+91");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    fullName: "",
    email: "",
    phoneNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Update fullName automatically when firstName or lastName changes
  useEffect(() => {
    const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
    setFormData((prev) => ({ ...prev, fullName }));
  }, [formData.firstName, formData.lastName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, phoneNumber } = formData;

    // Validation
    if (!firstName || !lastName || !email || !phoneNumber) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please fill all fields",
        confirmButtonText: "OK",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Email",
        text: "Please enter a valid email",
        showConfirmButton: false,
        timer: 1500, // disappears after 1.5 seconds
      });
      return;
    }

    if (phoneNumber.length !== 10) {
      Swal.fire({
        icon: "error",
        title: "Invalid Phone Number",
        text: "Phone number must be 10 digits",
        showConfirmButton: false,
        timer: 1500, // disappears after 1.5 seconds
      });
      return;
    }
    // If validation passes, simulate API call
    setIsLoading(true);
    try {
        const payload = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
          email: email.trim(),
          mobile: phoneNumber,
        };
      // console.log(payload);
      const res = await RegistrationAPI(payload);
      // const res = true;
      if(res?.success){
        Swal.fire({
          icon: "success",
          title: "Account Created",
          text: "Your account has been successfully created!",
          confirmButtonText: "OK",
        });
        navigate("/");
      }
      else{
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res?.message || "Something went wrong. Please try again.",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error?.message || "Something went wrong. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rp-create-account-container">
      {/* Left Section - Branding */}
      <div className="rp-create-account-left-section">
        <div className="rp-create-account-decorative-shape shape-1"></div>
        <div className="rp-create-account-decorative-shape shape-2"></div>
        <div className="rp-create-account-content">
          <div className="rp-create-account-logo-wrapper">
            <div className="rp-create-account-logo-glass-card">
              <img
                  src="/wealth_compounders_logo.png"
                alt="Logo"
                className="rp-create-account-logo-img"
              />
            </div>
          </div>
          <h1 className="rp-create-account-title">Join Us</h1>
          <p className="rp-create-account-subtitle">
            Start your wealth creation journey today.
          </p>
          <div className="rp-create-account-footer-text">
            <span>Already have an account? </span>
            <button onClick={() => navigate("/")}>Sign In</button>
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="rp-create-account-right-section">
        <div className="rp-create-account-form-container">
          <h2 className="rp-create-account-form-title">Create Account</h2>
          <p className="rp-create-account-form-subtitle">
            Enter your details to get started.
          </p>

          <form onSubmit={handleRegister}>
            {/* First Name */}
            <div className="rp-create-account-form-group">
              <label className="rp-create-account-label">First Name</label>
              <input
                type="text"
                name="firstName"
                className="rp-create-account-text-input"
                placeholder="Enter First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>

            {/* Last Name */}
            <div className="rp-create-account-form-group">
              <label className="rp-create-account-label">Last Name</label>
              <input
                type="text"
                name="lastName"
                className="rp-create-account-text-input"
                placeholder="Enter Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>

            {/* Full Name (readonly) */}
            <div className="rp-create-account-form-group">
              <label className="rp-create-account-label">Full Name</label>
              <input
              disabled
                type="text"
                name="fullName"
                placeholder="auto fill"
                className="rp-create-account-text-input"
                value={formData.fullName}
                readOnly      
              />
              
            </div>

            {/* Email */}
            <div className="rp-create-account-form-group">
              <label className="rp-create-account-label">Email Address</label>
              <input
                type="email"
                name="email"
                className="rp-create-account-text-input"
                placeholder="Enter Email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>

            {/* Phone */}
            <div className="rp-create-account-form-group">
              <label className="rp-create-account-label">Phone Number</label>
              <div className="rp-create-account-phone-input-container">
                <select
                  className="rp-create-account-country-code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                </select>
                <input
                  type="tel"
                  className="rp-create-account-phone-input"
                  placeholder="Enter Phone Number"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  maxLength="10"
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="rp-create-account-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? "Creating Account..." : "Verify & Create"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
